import {useContext, useState} from 'react'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {checkConstraintsFactory, formBuilderChangeFieldById, formBuilderPickFieldById} from '@s-ui/react-form-builder'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'

import {useLandingInstantOfferTracking} from '../../../../../useLandingInstantOfferTracking.js'
import InputAddonPlate from './InputAddonPlate.js'

const getClearedNumericValue = ({value}) => value?.replace(/\D/g, '')

const useFormPlate = ({onGetVehicles, sellerVehicle}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')

  const {trackFieldChange, trackSubmitPlateFormError} = useLandingInstantOfferTracking()

  const {
    fieldsToTagOnBlurValue,
    fieldsToTagOnChangeValue,
    formErrorTypes,
    formFieldIds,
    instantOfferLandingPlateFormConfig: {fields: formFields}
  } = config.get('form')

  const {kilometersId: kilometersFieldId, plate: plateFieldId, vehicleStatus: vehicleStatusFieldId} = formFieldIds

  const initialValues = {
    [kilometersFieldId]: '',
    [plateFieldId]: '',
    [vehicleStatusFieldId]: '',
    ...sellerVehicle
  }

  const [fields, setFields] = useState({...initialValues})
  const [formConfiguration, setFormConfiguration] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [validatedErrors, setValidatedErrors] = useState({})

  if (!formConfiguration) {
    setFormConfiguration(() => {
      for (const field of formFields) {
        const {hint, label, help, constraints, datalist} = field
        if (hint) field.hint = i18n.t(hint, 1)
        if (label) field.label = i18n.t(label, 1)
        if (help) field.help = i18n.t(help, 1)
        if (constraints) {
          constraints.forEach(constraint => {
            if (constraint.message) constraint.message = i18n.t(constraint.message, 1)
          })
        }
        if (datalist) {
          datalist.forEach(datalistItem => {
            datalistItem.text = i18n.t(datalistItem.text, 1)
          })
        }
      }

      return {
        form: {
          id: 'mt-instant-offer-landing-plate',
          type: 'group',
          label: '',
          fields: [
            {
              id: 'mt-instant-offer-landing-plate-fields',
              type: 'fieldset',
              display: 'block',
              hidden: false,
              fields: formFields
            }
          ]
        }
      }
    })
  }

  const SUIComponentsConfig = ({id}) => {
    const props = {
      [plateFieldId]: {leftAddon: <InputAddonPlate />, size: 'l'},
      [kilometersFieldId]: {
        autoComplete: 'off',
        inputMode: 'numeric',
        size: 'l'
      },
      [vehicleStatusFieldId]: {
        autoComplete: 'off',
        iconArrowDown: <IconChevronDown aria-hidden="true" role="presentation" size={atomIconSizes.medium} />,
        selectSize: 'l',
        size: 'large'
      }
    }
    return props[id]
  }

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formConfiguration)(checkParams)

    setErrors(_errors)
    return _errors
  }

  const handleOnBlur = fieldId => {
    checkConstraints({fieldId})

    if (fieldsToTagOnBlurValue.includes(fieldId)) {
      const {value} = formBuilderPickFieldById(formConfiguration?.form.fields, fieldId)
      value && trackFieldChange({fieldId})
    }
  }

  const handleOnChange = ({__FIELD_CHANGED__, __FORM_STATE__, ...newFields}) => {
    if (__FIELD_CHANGED__ === kilometersFieldId) {
      const clearedValue = getClearedNumericValue({value: newFields[kilometersFieldId]})
      const formattedValue = clearedValue && i18n.n(Number(clearedValue))

      newFields[kilometersFieldId] = formattedValue
      formBuilderChangeFieldById(formConfiguration.form.fields, kilometersFieldId, {
        value: formattedValue
      })
    } else if (__FIELD_CHANGED__ === plateFieldId) {
      const upperCasedPlate = newFields[plateFieldId].toUpperCase()
      const formattedValue = upperCasedPlate.replace(/[^A-Z0-9]/g, '')

      newFields[plateFieldId] = formattedValue
      formBuilderChangeFieldById(formConfiguration.form.fields, plateFieldId, {
        value: formattedValue
      })
    } else {
      checkConstraints({fieldId: __FIELD_CHANGED__})
    }

    if (fieldsToTagOnChangeValue.includes(__FIELD_CHANGED__)) {
      trackFieldChange({fieldId: __FIELD_CHANGED__})
    }

    setFields({...newFields})
  }

  const isValidForm = errors => !Object.values(errors).some(error => error.length > 0)

  const handleOnSubmit = () => {
    setIsLoading(true)

    const errors = checkConstraints()

    if (isValidForm(errors)) {
      domain
        .get('get_vehicles_info_by_plate_use_case')
        .execute({
          plate: fields[plateFieldId]
        })
        .then(useCaseResponse => {
          onGetVehicles({
            fields: {...fields, [kilometersFieldId]: getClearedNumericValue({value: fields[kilometersFieldId]})},
            getVehiclesResponse: useCaseResponse
          })
        })
    } else {
      trackSubmitPlateFormError({errorType: formErrorTypes.fieldError, errors})
      setIsLoading(false)
    }
  }

  return {
    formConfiguration,
    handleOnBlur,
    handleOnChange,
    handleOnSubmit,
    initialValues,
    isLoading,
    validatedErrors,
    setValidatedErrors,
    SUIComponentsConfig
  }
}

export default useFormPlate
